import { useState } from 'react';
import { Calendar, CheckCircle2, Mail, MessageSquare, Plus, Trash2 } from 'lucide-react';
import { Button, Card, CardHeader, CopyButton, ProviderLogo } from '@/components/ui';
import { ConfirmModal } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/Toast';
import SetupGuidePanel from '@/pages/integrations/SetupGuidePanel';
import {
  useGoogleConnections,
  useCreateGoogleConnection,
  useDeleteGoogleConnection,
  useDetectChatApp,
} from '@/hooks/useGoogleWatches';
import {
  useMyCredentials,
  useOrgCredentials,
  useStoreAppCredential,
  useVerifyCredential,
} from '@/hooks/useIdentityCredentials';
import GoogleConnectionStats from '@/pages/integrations/GoogleConnectionStats';
import type { Integration } from '@/types/integrations';
import type {
  CreateGoogleConnectionResult,
  GoogleConnectionConfig,
  GoogleSurface,
} from '@/types/google';

/** Snapshot of the values used to create a connection, for the setup script. */
interface CreatedConnection {
  result: CreateGoogleConnectionResult;
  surface: GoogleSurface;
  gcpProjectId: string;
  pubsubTopic: string;
  pushSa: string;
}

/** Pub/Sub topic name we prefill per surface (e.g. `optr-gmail-events`). */
const defaultTopicName = (surface: GoogleSurface) => `optr-${surface}-events`;

/** Derived push OIDC service-account email for a given project. */
const defaultPushSa = (projectId: string) =>
  projectId.trim() ? `optr-push@${projectId.trim()}.iam.gserviceaccount.com` : '';

/** Fully-qualified Pub/Sub topic from project id + topic name. */
const fullTopicPath = (projectId: string, topicName: string) =>
  projectId.trim() && topicName.trim()
    ? `projects/${projectId.trim()}/topics/${topicName.trim()}`
    : '';

/**
 * Org-admin Google Workspace connection management. Set up the org-level
 * connection (GCP project + Pub/Sub topic), then per-space "Connect Gmail/Chat"
 * attaches mailboxes/spaces under it. GCP provisioning is captured-and-scripted:
 * we show the setup-gcp.sh command for ops to run.
 */
export default function GoogleWorkspacePage() {
  const connectionsQ = useGoogleConnections();
  const [surface, setSurface] = useState<GoogleSurface>('gmail');
  const [gcpProjectId, setGcpProjectId] = useState('');
  // Topic name and push SA are derived from surface / project id unless the
  // admin overrides them; `*Touched` switches the field to the manual value.
  const [topicNameRaw, setTopicNameRaw] = useState('');
  const [topicTouched, setTopicTouched] = useState(false);
  const [pushSaRaw, setPushSaRaw] = useState('');
  const [pushSaTouched, setPushSaTouched] = useState(false);
  const [chatAppId, setChatAppId] = useState('');
  const [created, setCreated] = useState<CreatedConnection | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Integration | null>(null);
  const create = useCreateGoogleConnection();
  const remove = useDeleteGoogleConnection();
  const { success, error } = useToast();

  const topicName = topicTouched ? topicNameRaw : defaultTopicName(surface);
  const pushSa = pushSaTouched ? pushSaRaw : defaultPushSa(gcpProjectId);
  const pubsubTopic = fullTopicPath(gcpProjectId, topicName);

  // Calendar brokers push internally — no GCP project / Pub-Sub / chat-app inputs.
  const isCalendar = surface === 'calendar';
  const chatNeedsAppId = surface === 'chat' && !chatAppId.trim();
  const gcpMissing = !isCalendar && (!gcpProjectId.trim() || !pubsubTopic);

  const submit = async () => {
    if (gcpMissing || chatNeedsAppId) return;
    try {
      const res = await create.mutateAsync(
        isCalendar
          ? { surface }
          : {
              surface,
              gcpProjectId: gcpProjectId.trim(),
              pubsubTopic,
              ...(surface === 'chat' && chatAppId.trim() ? { chatAppId: chatAppId.trim() } : {}),
              ...(pushSa.trim() ? { pushServiceAccountEmail: pushSa.trim() } : {}),
            },
      );
      setCreated({
        result: res,
        surface,
        gcpProjectId: gcpProjectId.trim(),
        pubsubTopic,
        pushSa: pushSa.trim() || defaultPushSa(gcpProjectId),
      });
      success(
        isCalendar
          ? 'Connection created — finish the GCP prerequisites below'
          : 'Connection created — run the setup script below',
      );
    } catch (e) {
      error(e instanceof Error ? e.message : 'Could not create connection');
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.integrationId);
      success(`Removed ${pendingDelete.displayName}`);
      setPendingDelete(null);
    } catch (e) {
      error(e instanceof Error ? e.message : 'Could not remove connection');
    }
  };

  const connections = connectionsQ.data ?? [];

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4">
        <ProviderLogo
          slug="google-workspace"
          displayName="Google Workspace"
          className="h-12 w-12"
          fallbackClassName="text-sm"
        />
        <div>
          <h1 className="text-2xl font-bold text-foreground">Google Workspace</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            One connection per surface (Gmail / Chat / Calendar) per org. After
            creating it, finish the printed GCP setup, then users connect their own
            mailboxes / spaces / calendars from their space.
          </p>
        </div>
      </div>

      <GoogleConnectionStats connections={connections} />

      <Card>
        <CardHeader title="Existing connections" />
        {connectionsQ.isLoading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : connections.length === 0 ? (
          <p className="text-xs text-muted-foreground">No connections yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {connections.map((c) => {
              const cfg = (c.config ?? {}) as GoogleConnectionConfig;
              const isCalendarConn = c.provider === 'google-calendar';
              const Icon = isCalendarConn
                ? Calendar
                : c.provider === 'google-chat'
                  ? MessageSquare
                  : Mail;
              return (
                <li key={c.integrationId} className="py-5 text-sm first:pt-0 last:pb-0">
                 <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <span className="font-medium text-foreground">{c.displayName}</span>{' '}
                      <span className="text-muted-foreground">({c.provider})</span>
                      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                        {isCalendarConn ? (
                          <>
                            <dt>Surface</dt>
                            <dd className="truncate text-foreground/80">
                              Calendar (push brokered internally)
                            </dd>
                          </>
                        ) : (
                          <>
                            <dt>Project</dt>
                            <dd className="truncate font-mono text-foreground/80">
                              {cfg.gcpProjectId || '—'}
                            </dd>
                            <dt>Topic</dt>
                            <dd className="truncate font-mono text-foreground/80">
                              {cfg.pubsubTopic || '—'}
                            </dd>
                            <dt>Service account</dt>
                            <dd className="truncate font-mono text-foreground/80">
                              {cfg.oidc?.serviceAccountEmail || '—'}
                            </dd>
                          </>
                        )}
                        {c.provider === 'google-chat' && (
                          <>
                            <dt>Chat app id</dt>
                            <dd className="truncate font-mono text-foreground/80">
                              {cfg.chatAppId || '—'}
                            </dd>
                          </>
                        )}
                      </dl>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    onClick={() => setPendingDelete(c)}
                    aria-label={`Remove ${c.displayName}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                 </div>
                 {c.provider === 'google-chat' && (
                   <div className="mt-3 pl-8">
                     <ChatConnectionCredential chatAppId={cfg.chatAppId} />
                   </div>
                 )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader title="Create a connection" />
        <div className="grid gap-3 max-w-2xl">
          <label className="text-sm text-muted-foreground">
            Surface
            <select
              className="mt-1 block w-full text-sm border border-border rounded-md bg-background px-2 py-1.5"
              value={surface}
              onChange={(e) => setSurface(e.target.value as GoogleSurface)}
            >
              <option value="gmail">Gmail</option>
              <option value="chat">Google Chat</option>
              <option value="calendar">Google Calendar</option>
            </select>
          </label>
          {isCalendar && (
            <p className="text-xs text-muted-foreground">
              Calendar push is brokered internally — no GCP project, Pub/Sub topic,
              or service account to provide. After creating the connection, finish
              the two GCP prerequisites shown below.
            </p>
          )}
          {!isCalendar && (
            <>
              <label className="text-sm text-muted-foreground">
                GCP project id
                <input
                  className="mt-1 block w-full text-sm border border-border rounded-md bg-background px-2 py-1.5"
                  value={gcpProjectId}
                  onChange={(e) => setGcpProjectId(e.target.value)}
                  placeholder="my-customer-project"
                />
              </label>
              <label className="text-sm text-muted-foreground">
                Pub/Sub topic name
                <input
                  className="mt-1 block w-full text-sm border border-border rounded-md bg-background px-2 py-1.5"
                  value={topicName}
                  onChange={(e) => {
                    setTopicTouched(true);
                    setTopicNameRaw(e.target.value);
                  }}
                  placeholder={defaultTopicName(surface)}
                />
                <span className="mt-1 block font-mono text-xs text-muted-foreground">
                  {pubsubTopic || `projects/<project>/topics/${topicName || '<topic>'}`}
                </span>
              </label>
            </>
          )}
          {surface === 'chat' && (
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">
                Operator Chat app id
                <input
                  className="mt-1 block w-full text-sm border border-border rounded-md bg-background px-2 py-1.5"
                  value={chatAppId}
                  onChange={(e) => setChatAppId(e.target.value)}
                  placeholder="users/1234567890"
                />
                <span className="mt-1 block text-xs text-muted-foreground">
                  The Operator Chat app's bot user resource — used to auto-create a
                  user's DM with Operator and to detect @Operator mentions.
                </span>
              </label>
              <ChatAppDetector onPick={setChatAppId} />
            </div>
          )}
          {!isCalendar && (
            <label className="text-sm text-muted-foreground">
              Push service-account email (Gmail OIDC)
              <input
                className="mt-1 block w-full text-sm border border-border rounded-md bg-background px-2 py-1.5"
                value={pushSa}
                onChange={(e) => {
                  setPushSaTouched(true);
                  setPushSaRaw(e.target.value);
                }}
                placeholder={defaultPushSa('my-customer-project')}
              />
              <span className="mt-1 block text-[11px] text-muted-foreground">
                Auto-generated from the project id; edit if your service account differs.
              </span>
            </label>
          )}
          <div>
            <Button
              size="sm"
              onClick={submit}
              isLoading={create.isPending}
              disabled={gcpMissing || chatNeedsAppId}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Create connection
            </Button>
          </div>
        </div>
      </Card>

      {created &&
        (created.surface === 'calendar' ? (
          <CalendarPrerequisites />
        ) : (
          <SetupScript created={created} />
        ))}

      <ConfirmModal
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        isLoading={remove.isPending}
        variant="destructive"
        title="Remove connection"
        message={`Remove "${pendingDelete?.displayName}"? Existing per-space watches under it will stop receiving events.`}
        confirmText="Remove"
      />
    </div>
  );
}

const CHAT_BOT_SCOPE = 'https://www.googleapis.com/auth/chat.bot';

/**
 * Per-connection "reply as Operator" credential, rendered inside a Google Chat
 * connection row. The connection already carries the Operator Chat app id
 * (`chatAppId`); the credential is linked to it by that id:
 *  - not configured → paste the service-account key + Save (the chat app id is
 *    taken from the connection, not re-entered);
 *  - configured → show a status bar + a live "Test connection" (force-mints a
 *    chat.bot token server-side). The key is stored encrypted in optr-identity
 *    and never returned to the browser.
 */
function ChatConnectionCredential({ chatAppId }: { chatAppId?: string }) {
  const store = useStoreAppCredential();
  const orgCredsQ = useOrgCredentials();
  const verify = useVerifyCredential();
  const { success, error: errorToast } = useToast();
  const [saKeyJson, setSaKeyJson] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Link the org's app credential to THIS connection by chat app id.
  const appCred = (orgCredsQ.data?.credentials ?? []).find(
    (c) =>
      c.provider === 'google-chat' &&
      c.principal.kind === 'app' &&
      (!chatAppId || c.metadata.chatAppId === chatAppId),
  );

  const runTest = async () => {
    if (!appCred) return;
    try {
      const res = await verify.mutateAsync(appCred.credId);
      if (res.ok) success('Operator can post as the bot — chat.bot token minted ✓');
      else errorToast(`Test failed: ${res.reason}`);
    } catch (e) {
      errorToast(e instanceof Error ? e.message : 'Test failed');
    }
  };

  // Light client-side validation; optr-identity re-validates authoritatively.
  const saError = (() => {
    const trimmed = saKeyJson.trim();
    if (trimmed.length === 0) return null;
    try {
      const parsed = JSON.parse(trimmed) as { client_email?: string; private_key?: string };
      if (!parsed.client_email || !parsed.private_key) {
        return 'Key is missing client_email / private_key.';
      }
      return null;
    } catch {
      return 'Not valid JSON.';
    }
  })();

  const canSubmit = saKeyJson.trim().length > 0 && !saError && !!chatAppId && !store.isPending;

  const submit = async () => {
    if (!chatAppId) return;
    setError(null);
    try {
      await store.mutateAsync({
        provider: 'google-chat',
        saKeyJson: saKeyJson.trim(),
        scopes: [CHAT_BOT_SCOPE],
        displayName: 'Operator Chat app',
        metadata: { chatAppId },
      });
      success('Operator Chat app credential saved');
      setSaKeyJson('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the credential');
    }
  };

  // Configured: status bar + live test, no input.
  if (appCred) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2">
        <span className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          Reply as Operator — configured
        </span>
        <Button size="sm" variant="outline" onClick={runTest} isLoading={verify.isPending}>
          Test connection
        </Button>
      </div>
    );
  }

  // Connection has no chat app id (shouldn't happen — creation requires it).
  if (!chatAppId) {
    return (
      <p className="text-xs text-muted-foreground">
        Set the Operator Chat app id on this connection to enable replying as Operator.
      </p>
    );
  }

  // Not configured: paste the SA key; the chat app id comes from the connection.
  return (
    <div className="grid gap-2 max-w-2xl rounded-md border border-dashed border-border p-3">
      <p className="text-sm text-muted-foreground">
        To let agents reply <em>as Operator</em> (not as the connected user), paste the
        Operator Chat app's Google service-account key. Stored encrypted; only short-lived{' '}
        <code>chat.bot</code> tokens are minted from it.
      </p>
      <textarea
        className="block w-full font-mono text-xs border border-border rounded-md bg-background px-2 py-1.5 h-32"
        value={saKeyJson}
        onChange={(e) => setSaKeyJson(e.target.value)}
        placeholder='{ "type": "service_account", "client_email": "...", "private_key": "-----BEGIN PRIVATE KEY-----\n..." }'
        spellCheck={false}
      />
      {saError && <span className="text-xs text-destructive">{saError}</span>}
      {error && <span className="text-xs text-destructive">{error}</span>}
      <div>
        <Button size="sm" onClick={submit} isLoading={store.isPending} disabled={!canSubmit}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Save credential
        </Button>
      </div>
    </div>
  );
}

/**
 * "Detect Operator app" helper. Uses the admin's own connected Google account to
 * list their Chat bot DMs (backend) and surfaces them as pickable candidates,
 * so the admin doesn't have to hand-type the Operator app's 21-digit bot user
 * id. It can't bootstrap a DM that doesn't exist — if none are found, the admin
 * messages Operator once in Chat, then retries.
 */
function ChatAppDetector({ onPick }: { onPick: (appId: string) => void }) {
  const credsQ = useMyCredentials();
  const detect = useDetectChatApp();
  const [accountId, setAccountId] = useState<string | undefined>(undefined);

  const accounts = (credsQ.data?.credentials ?? []).filter(
    (c) => c.provider === 'google' && c.status === 'connected',
  );
  const selectedAccountId = accountId ?? accounts[0]?.providerInstanceId ?? undefined;
  const candidates = detect.data ?? [];

  if (!credsQ.isLoading && accounts.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Connect a Google account (in your Credentials) to auto-detect the Operator
        app id.
      </p>
    );
  }

  return (
    <div className="rounded-md border border-border bg-muted/30 p-2.5 space-y-2">
      <div className="flex items-center gap-2">
        {accounts.length > 1 && (
          <select
            className="text-xs border border-border rounded-md bg-background px-2 py-1"
            value={selectedAccountId}
            onChange={(e) => setAccountId(e.target.value)}
            aria-label="Google account to detect with"
          >
            {accounts.map((a) => (
              <option key={a.credId} value={a.providerInstanceId}>
                {a.metadata.externalUsername ?? a.metadata.accountName ?? a.credId}
              </option>
            ))}
          </select>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => detect.mutate(selectedAccountId)}
          isLoading={detect.isPending}
          disabled={credsQ.isLoading}
        >
          Detect Operator
        </Button>
        <span className="text-[11px] text-muted-foreground">
          Lists Chat apps you’ve DM’d, so you can pick Operator.
        </span>
      </div>

      {detect.isError && (
        <p className="text-xs text-red-600 dark:text-red-400">
          {detect.error instanceof Error ? detect.error.message : 'Detection failed'}
        </p>
      )}

      {detect.isSuccess && candidates.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No Chat apps found. Open Google Chat, message Operator once (or add it to
          a space), then click Detect again.
        </p>
      )}

      {candidates.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border bg-card">
          {candidates.map((c) => (
            <li key={c.appId} className="flex items-center gap-2 px-2 py-1.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs text-foreground">
                  {c.snippet || '(no recent messages)'}
                </p>
                <p className="truncate font-mono text-[11px] text-muted-foreground">
                  {c.appId}
                  {c.lastActiveTime
                    ? ` · ${new Date(c.lastActiveTime).toLocaleDateString()}`
                    : ''}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => onPick(c.appId)}>
                Use this
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Calendar's post-create panel. Unlike Gmail/Chat, the admin pastes no push
 * token and runs no gcloud script — push is brokered internally. Two manual GCP
 * prerequisites remain (enable the Calendar API, verify the webhook domain);
 * those are sourced from the backend setup-guide markdown rather than hardcoded.
 */
function CalendarPrerequisites() {
  return (
    <section className="space-y-3">
      <p className="flex items-center gap-2 text-sm text-foreground">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        Connection created. Finish these one-time GCP prerequisites before
        connecting calendars:
      </p>
      <SetupGuidePanel
        provider="google-calendar"
        providerDisplayName="Google Calendar"
      />
    </section>
  );
}

function SetupScript({ created }: { created: CreatedConnection }) {
  const { result, surface, gcpProjectId, pushSa } = created;
  const webhookUrl =
    result.webhookUrl ??
    `https://<your-optr-webhook-host>/webhooks/${result.provider}/${result.webhookId}`;
  const pushUrl = `${webhookUrl}?token=${result.pushToken}`;
  const script = [
    'scripts/google/setup-gcp.sh \\',
    `  --project ${gcpProjectId} \\`,
    `  --surface ${surface} \\`,
    `  --webhook-url "${pushUrl}" \\`,
    `  --push-sa "${pushSa}"`,
  ].join('\n');

  return (
    <section className="border border-amber-500/40 rounded-lg bg-amber-500/5 p-4 space-y-2">
      <h2 className="text-sm font-semibold text-foreground">
        Run this once in your GCP project
      </h2>
      <p className="text-xs text-muted-foreground">
        The push token is shown only once — it's embedded in the endpoint URL below.
      </p>
      <pre className="text-[11px] bg-background border border-border rounded-md p-3 overflow-x-auto whitespace-pre">
        {script}
      </pre>
      <CopyButton value={script} label="Copy" successLabel="Copied" />
    </section>
  );
}
